import { useEffect, useMemo, useState } from "react";
import { HiChevronDown, HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import { LuDownload } from "react-icons/lu";
import * as XLSX from "xlsx";
import { useUser } from "../../../../context/UserContext.tsx";
import { formatCurrency } from "../../../../helpers/functions.tsx";
import {
    formatAdminFiguresDateLabel,
    getAdminFiguresIsoWeekId,
} from "../../../../helpers/adminFiguresTime.tsx";
import {
    getAdminWeekDays,
    getAdminWeekRangeWeeksAgo,
} from "../../../../helpers/adminWeeklyWeekUtils.tsx";
import {
    CasinoWeeklyStat,
    readCasinoWeeklyStats,
} from "../../../../services/admin/VimplayStatsService.tsx";
import "../../../../styles/admin/CasinoStats.css";

const MAX_WEEKS_BACK = 26;

const money = (value: number) => {
    const amount = `$${formatCurrency(Math.abs(value))}`;
    return value < 0 ? `-${amount}` : amount;
};

const amountTone = (value: number) => {
    if (value > 0) return "casino-stat-pos";
    if (value < 0) return "casino-stat-neg";
    return "";
};

function CasinoWeeklyStats() {
    const { user: currentUser } = useUser();
    const [weeksAgo, setWeeksAgo] = useState(0);
    const [expanded, setExpanded] = useState(true);
    const [owner, setOwner] = useState<CasinoWeeklyStat | null>(null);
    const [agents, setAgents] = useState<CasinoWeeklyStat[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    const range = useMemo(() => getAdminWeekRangeWeeksAgo(weeksAgo), [weeksAgo]);
    const weekDays = useMemo(() => getAdminWeekDays(range.start), [range.start]);
    const weekId = useMemo(() => getAdminFiguresIsoWeekId(range.start), [range.start]);
    const weekLabel = `${formatAdminFiguresDateLabel(weekDays[0], { month: "short", day: "numeric" })} – ${formatAdminFiguresDateLabel(weekDays[6], { month: "short", day: "numeric", year: "numeric" })}`;

    useEffect(() => {
        const userName = currentUser?.userName?.trim();
        if (!userName) {
            setOwner(null);
            setAgents([]);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setErrorMessage("");
            try {
                const view = await readCasinoWeeklyStats(userName, weekId);
                if (cancelled) return;
                setOwner(view.owner);
                setAgents(view.agents);
            } catch (error) {
                console.error(error);
                if (!cancelled) {
                    setOwner(null);
                    setAgents([]);
                    setErrorMessage("Unable to load casino statistics for this week.");
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [currentUser?.userName, weekId]);

    const parentName = owner?.ownerUserName || currentUser?.userName || "Account";
    const canExpand = agents.length > 0;

    const exportRows = () => {
        if (!owner) return;
        const exportLine = (row: CasinoWeeklyStat) => ({
            "Agent Name": row.ownerUserName,
            "Active Players": row.activePlayers,
            Win: row.win,
            Loss: row.loss,
            Total: row.total,
            "Live Used": row.liveUsed ? "YES" : "NO",
            "Live Win/Loss": row.liveNet,
        });
        const sheetRows = [exportLine(owner), ...agents.map(exportLine)];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheetRows), "Casino");
        XLSX.writeFile(workbook, `CasinoWeekly_${weekLabel.replace(/[^\w]+/g, "_")}.xlsx`);
    };

    const renderTotals = (row: CasinoWeeklyStat) => (
        <>
            <td>{row.activePlayers.toLocaleString("en-US")}</td>
            <td className={amountTone(row.win)}>{money(row.win)}</td>
            <td className={row.loss > 0 ? "casino-stat-neg" : ""}>{money(row.loss)}</td>
            <td className={amountTone(row.total)}>{money(row.total)}</td>
            <td>
                <span className={`casino-stat-pill ${row.liveUsed ? "is-yes" : "is-no"}`}>
                    {row.liveUsed ? "YES" : "NO"}
                </span>
            </td>
            <td className={amountTone(row.liveNet)}>{money(row.liveNet)}</td>
        </>
    );

    return (
        <section className="casino-stats">
            <div className="casino-stats-header">
                <div>
                    <h1 className="casino-stats-title">Weekly Statistics</h1>
                    <p className="casino-stats-subtitle">Total = Win – Loss</p>
                </div>
                <div className="casino-stats-tools">
                    <div className="casino-stats-week">
                        <button
                            type="button"
                            aria-label="Previous week"
                            disabled={weeksAgo >= MAX_WEEKS_BACK}
                            onClick={() => setWeeksAgo((current) => Math.min(MAX_WEEKS_BACK, current + 1))}
                        >
                            <HiChevronLeft />
                        </button>
                        <span>{weekLabel}</span>
                        <button
                            type="button"
                            aria-label="Next week"
                            disabled={weeksAgo === 0}
                            onClick={() => setWeeksAgo((current) => Math.max(0, current - 1))}
                        >
                            <HiChevronRight />
                        </button>
                    </div>
                    <button type="button" className="casino-stats-export" onClick={exportRows} disabled={isLoading || !owner}>
                        <LuDownload />
                        Export
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className="casino-stats-status">Loading this week...</div>
            ) : errorMessage ? (
                <div className="casino-stats-status casino-stat-neg">{errorMessage}</div>
            ) : owner ? (
                <div className="casino-stats-table-wrap">
                    <table className="casino-stats-table">
                        <thead>
                            <tr>
                                <th>Agent Name</th>
                                <th>Active Players</th>
                                <th>Win</th>
                                <th>Loss</th>
                                <th>Total</th>
                                <th>Live Used</th>
                                <th>Live Win/Loss</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="casino-stats-parent">
                                <td>
                                    {canExpand ? (
                                        <button
                                            type="button"
                                            className="casino-stats-agent"
                                            aria-expanded={expanded}
                                            onClick={() => setExpanded((current) => !current)}
                                        >
                                            <HiChevronDown className={expanded ? "" : "is-collapsed"} />
                                            <span>{parentName}</span>
                                        </button>
                                    ) : (
                                        <span className="casino-stats-agent-name">{parentName}</span>
                                    )}
                                </td>
                                {renderTotals(owner)}
                            </tr>
                            {canExpand && expanded && agents.map((row) => (
                                <tr key={row.ownerUserName}>
                                    <td>
                                        <span className="casino-stats-child">{row.ownerUserName}</span>
                                    </td>
                                    {renderTotals(row)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </section>
    );
}

export default CasinoWeeklyStats;
